/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { faPen, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Dropdown, DropdownButton, Modal, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { proxyAPI } from "../proxyAPI.ts";
import ContactCard from "../Contacts/ContactCard";
import RealTimeReportingIcon from "../../assets/icons/realtime_reporting.svg2";
import Subheading from "../Common/Subheading";
import Report from "./RealtimeReporting/Report";
import BoomLogicWFMessaging from "../Dashboard/BoomLogicWFMessaging";
import BroadcastMessaging from "../Dashboard/BroadcastMessaging";
import InboundCampaignMessaging from "../Dashboard/InboundCampaignMessaging";
import SchedulersList from "../Schedulers/SchedulersList.tsx";

const reportingViews = [
  { value: "hub", label: "Message hub" },
  { value: "transactions", label: "Transactions" },
];

export const ContactPopover = React.forwardRef(
  ({ popper, children, show: _, ...props }, ref) => {
    const [contact, setContact] = useState(null);

    useEffect(() => {
      const getContact = async () => {
        const data = await proxyAPI({
          prefix: "contacts/v1",
          url: `/first/${props.uuid}?fields.cards=1&fields.channels=1`,
        });
        setContact(data);
      };

      getContact();
    }, []);

    const contactFields =
      contact?.details
        .filter((d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice)
        .filter((d) => d.values[0].data || d.values[0].option.label)
        .slice(0, 2)
        .map((d) => d.field) ?? [];

    useEffect(() => {
      popper.scheduleUpdate();
    }, [contact, popper]);

    return (
      <Popover ref={ref} body {...props}>
        <div className="overflow-scroll">
          <table>
            <thead>
              <tr>
                <th className="px-2" />
                <th className="px-2" />
                {contactFields.map((cf) => (
                  <th className="px-2" key={cf.uuid}>
                    {cf.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contact && (
                <ContactCard
                  contact={contact}
                  contactButton={() => (
                    <Link to={`/assets/contacts/edit/${props.uuid}`}>
                      <FontAwesomeIcon icon={faPen} />
                    </Link>
                  )}
                  contactFields={contactFields}
                  fullView={false}
                />
              )}
            </tbody>
          </table>
        </div>
      </Popover>
    );
  }
);

export function contactOption({ selectOption, ...props }) {
  return (
    <div className="border-bottom">
      <table>
        <tbody>
          <ContactCard
            contact={props.data.contact}
            contactFields={props.data.contact.details
              .filter(
                (d) => !d.field.isSms && !d.field.isEmail && !d.field.isVoice
              )
              .filter((d) => d.values[0].data || d.values[0].option.label)
              .slice(0, 2)
              .map((d) => d.field)}
            contactButton={() => (
              <button
                type="button"
                className="btn btn-outline-primary border-0"
                onClick={() => selectOption(props.data)}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            )}
          />
        </tbody>
      </table>
    </div>
  );
}

export function contactMenuList(props) {
  return (
    <div style={{ ...props.style, height: "100%", overflow: "auto" }}>
      <table className="table table-condensed bg-white">
        <tbody>{props.children}</tbody>
      </table>
    </div>
  );
}

export default function RealtimeReportingPage() {
  const { realtimeTab } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { projectUUID, broadcastUUID, inboundUUID, startDate, comms, messageStatus } =
    Object.fromEntries(sp.entries());
  const activeView = realtimeTab === "transactions" ? "transactions" : "hub";

  return (
    <div>
      <Subheading
        Icon={RealTimeReportingIcon}
        heading="Message hub"
        subheading="View and manage messaging activity in one place."
      />

      <div className="mb-3">
        <DropdownButton
          id="realtime-reporting-view-dropdown"
          title={reportingViews.find((view) => view.value === activeView)?.label}
          variant="colored"
        >
          {reportingViews.map((view) => (
            <Dropdown.Item
              as="button"
              key={view.value}
              onClick={() =>
                navigate(
                  view.value === "hub"
                    ? "/reporting/realtime"
                    : `/reporting/realtime/${view.value}`
                )
              }
            >
              {view.label}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>

      {activeView === "transactions" ? (
        <Report
          projectUUID={projectUUID}
          broadcastUUID={broadcastUUID}
          inboundUUID={inboundUUID}
          startDate={startDate}
          comms={comms}
          messageStatus={messageStatus}
        />
      ) : (
        <div className="d-flex flex-column gap-4">
          <section className="rounded-3 bg-white p-3">
            <h2 className="h5 fw-bold mb-3">Component messaging</h2>
            <BoomLogicWFMessaging showTransactions />
          </section>

          <section className="rounded-3 bg-white p-3">
            <h2 className="h5 fw-bold mb-3">Broadcast messaging</h2>
            <BroadcastMessaging showTransactions />
          </section>

          <section className="rounded-3 bg-white p-3">
            <h2 className="h5 fw-bold mb-3">Inbound campaigns</h2>
            <InboundCampaignMessaging showTransactions />
          </section>

          <section className="rounded-3 bg-white p-3">
            <h2 className="h5 fw-bold mb-3">Component schedulers</h2>
            <SchedulersList schedulerType="project" embedded />
          </section>

          <section className="rounded-3 bg-white p-3">
            <h2 className="h5 fw-bold mb-3">Broadcast schedulers</h2>
            <SchedulersList schedulerType="broadcaster" embedded />
          </section>
        </div>
      )}
      {(projectUUID || inboundUUID || broadcastUUID) && (
        <Modal show onHide={() => navigate("/reporting/realtime")} size="xxl">
          <Modal.Header closeButton>
            <Modal.Title>Detailed Report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Report
              hideFilters
              projectUUID={projectUUID}
              broadcastUUID={broadcastUUID}
              inboundUUID={inboundUUID}
              startDate={startDate}
              comms={comms}
              messageStatus={messageStatus}
            />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}
